import isCore from 'common/util/isCore';
import isFree from 'common/util/isFree';
import isGrowth from 'common/util/isGrowth';
import isPro from 'common/util/isPro';
import isStarter from 'common/util/isStarter';

import type { Discount } from 'common/api/endpoints/companies';
import type { Company } from 'common/containers/CompanyContainer';

export const PlanNames = {
  business: 'Business',
  coreAnnual: 'Core (Yearly)',
  coreMonthly: 'Core (Monthly)',
  growthAnnual: 'Growth (Yearly)',
  growthMonthly: 'Growth (Monthly)',
  proAnnual: 'Pro (Yearly)',
  proMonthly: 'Pro (Monthly)',
  starterAnnual: 'Starter (Yearly)',
  starterMonthly: 'Starter (Monthly)',
  free: 'Free',
} as const;

const isBusiness = (planID: string) => {
  return planID === 'business-trial-1' || /f\d\d/.test(planID);
};

export const getPlanName = (planID: string | null) => {
  if (!planID) {
    return null;
  }

  if (isFree(planID)) {
    return PlanNames.free;
  }

  if (isCore(planID)) {
    return planID.endsWith('yearly') ? PlanNames.coreAnnual : PlanNames.coreMonthly;
  }

  if (isPro(planID)) {
    return planID.endsWith('yearly') ? PlanNames.proAnnual : PlanNames.proMonthly;
  }

  if (isStarter(planID)) {
    return planID.endsWith('annual') ? PlanNames.starterAnnual : PlanNames.starterMonthly;
  }

  if (isGrowth(planID)) {
    return planID.endsWith('annual') ? PlanNames.growthAnnual : PlanNames.growthMonthly;
  }

  if (isBusiness(planID)) {
    return PlanNames.business;
  }

  return 'Custom';
};

const ReferralDiscounts = {
  monthly: {
    allowedPlanIDs: null,
    amountOff: null,
    couponID: 'monthly-referral-coupon',
    duration: 'repeating',
    durationInMonths: 3,
    percentOff: 20,
  },
  yearly: {
    allowedPlanIDs: null,
    amountOff: null,
    couponID: 'annual-referral-coupon',
    duration: 'once',
    durationInMonths: 12,
    percentOff: 5,
  },
} as const;

// Note: Referral program gives 20% off for 3 months (or %5 for annual plans) however discount is created after user buys a plan
// So we need to manually mock the discount Discount methods, if user has a referral code and did not buy before
export const getCompanyDiscount = (
  { discount, referee }: Company,
  billingCycle: 'monthly' | 'yearly',
) => {
  if (!discount && (!referee || referee.couponUsed)) {
    return null;
  }

  return discount ?? ReferralDiscounts[billingCycle];
};

// price and discount.amountOff are in cents (e.g. 1000 for $10)
export const getDiscountedPrice = (price: number, discount: Discount | null) => {
  if (!discount) {
    return price;
  }

  if (discount.amountOff) {
    return Math.max(0, price - discount.amountOff);
  } else if (discount.percentOff) {
    return Math.round((price * (100 - discount.percentOff)) / 100);
  }

  return price;
};
